/**
 * Shared utilities for slot components
 *
 * This file contains common utility functions and components used across
 * different slot types (insight, report, content refresh, content outline, etc.)
 */

import React from 'react';
import { motion } from 'motion/react';
import { ArrowUpRight, Eye } from 'lucide-react';
import { Button } from '@strivelabs/ui-core';
import { SLOT_ANIMATION, SLOT_CONTAINER_CLASSES } from '@/constants/reports';
import { ProcessingStatus } from '@/hooks/useKeyValOperations';
import type { InsightCardBlock, ReportBlock } from '@/types/ui-blocks';

/**
 * Shared slot container component with consistent animation and styling.
 * Wraps content in a motion.div with standard slot appearance using constants.
 *
 * @param children - Content to render inside the slot
 * @param slotRef - Optional ref to attach to the container
 */
export function SlotContainer({
  children,
  slotRef,
}: {
  children: React.ReactNode;
  slotRef?: React.RefObject<HTMLDivElement>;
}) {
  return (
    <motion.div
      ref={slotRef}
      {...SLOT_ANIMATION}
      className={SLOT_CONTAINER_CLASSES}
    >
      {children}
    </motion.div>
  );
}

/**
 * Parses keywords from various formats (array or comma-separated string) into a string array
 *
 * @param keywords - Keywords as array or comma-separated string
 * @returns Array of trimmed, non-empty keyword strings
 */
export function parseKeywords(keywords: unknown): string[] {
  if (Array.isArray(keywords)) {
    return keywords;
  }
  if (typeof keywords === 'string') {
    return keywords.split(',').map(k => k.trim()).filter(k => k.length > 0);
  }
  return [];
}

/**
 * Renders a simplified action indicator for content slots (refresh/outline).
 * Completed/review rows show a "View Details" link that navigates to the agent page.
 * In-progress rows show a spinner. Pending/failed rows show text-only status.
 *
 * @param status - Current processing status of the task
 * @param onNavigateToAgent - Callback to navigate to the full agent page
 */
export function getSlotActionButton({
  status,
  onNavigateToAgent,
}: {
  status: ProcessingStatus;
  onNavigateToAgent: () => void;
}) {
  if (status === ProcessingStatus.COMPLETED) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={onNavigateToAgent}
        className="h-auto p-0 text-sm text-gray-600 hover:text-gray-900 hover:bg-transparent"
      >
        View Details
        <ArrowUpRight className="w-4 h-4" />
      </Button>
    );
  }
  if (status === ProcessingStatus.WAITING_FOR_APPROVAL) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={onNavigateToAgent}
        className="h-auto p-0 text-sm text-blue-600 hover:text-blue-900 hover:bg-transparent"
      >
        Review Now
        <Eye className="w-4 h-4" />
      </Button>
    );
  }
  if (status === ProcessingStatus.IN_PROGRESS) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-amber-600">
        <span className="w-2 h-2 rounded-full bg-amber-500" />
        Processing
      </span>
    );
  }
  if (status === ProcessingStatus.FAILED) {
    return (
      <span className="w-full text-center text-sm text-red-600">-</span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-sm text-gray-600">Pending</span>
  );
}

/**
 * Downloads a PDF from an S3 signed URL
 *
 * @param url - S3 signed URL for the PDF
 * @param filename - Filename for the download
 * @returns Promise that resolves when download is complete
 */
export async function downloadPdfFromUrl(url: string, filename: string): Promise<void> {
  if (!url || !url.trim()) {
    throw new Error('Invalid PDF URL');
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to download PDF');
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the blob URL
  window.URL.revokeObjectURL(blobUrl);
}

/**
 * Type guard to check if a block is an InsightCardBlock.
 *
 * Uses the more robust check for 'block_elements' property rather than the optional 'type' property,
 * since the 'type' field is optional in the InsightCardBlock type definition.
 *
 * @param block - Block to check
 * @returns True if the block is an InsightCardBlock
 */
export const isInsightCardBlock = (block: ReportBlock | unknown): block is InsightCardBlock =>
  typeof block === 'object' &&
  block !== null &&
  'block_elements' in block &&
  Array.isArray(block.block_elements);
